import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FaCalendarAlt, FaExpand, FaStar } from 'react-icons/fa';
import { IoEye } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import { formatNumber } from '../../helper';
import Narray8Comment from '../../Components/Naray8/Comments/Narray8Comment';
import { PiShareFatFill } from 'react-icons/pi';
import Naray8BackButton from '../../Components/Naray8/ui/Naray8BackButton';
import { capitalizeFirstLetter } from '../../utils/stringUtils';
import { useAllNaray8Chapters, useNaray8ChapterById, useGetNaray8byId } from '../../hooks/api/useNaray8';
import { GiHamburgerMenu } from 'react-icons/gi';
import ClickOutsideWrapper from '../../Components/ModalComponents/ClickOutsideWrapper';
import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from 'react-icons/md';
import { useSelector } from 'react-redux';
import FollowButton from '../../Components/Naray8/ui/FollowButton';
import ChapterPurchaseFlow from '../../Components/Naray8/Modals/ChapterModalPurchases';
import { useQueryClient } from '@tanstack/react-query';
import ShareUsersModal from '../../Components/Naray8/Modals/ShareUsersModal';
import { checkFollowStatus } from '../../redux/naray8/naray8Action';

const Badge = ({ icon, text }) => (
    <div className="flex items-center gap-1 bg-white/15 px-3 py-1 rounded-[4px]">
        {icon}
        <span className="font-sora text-sm -mb-0.5">{text}</span>
    </div>
);

function ChapterPage() {

    const { chapterId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const myId = useSelector((state) => state.user.userData.userId);

    const { data: chapter, refetch: refetchChapter } = useNaray8ChapterById(chapterId);
    const { data: chapters, refetch: refetchChapters } = useAllNaray8Chapters(chapter?.narrate?.narrateId);

    // ✅ FIX #1: Fetch fresh book data separately
    const narrateId = chapter?.narrate?.narrateId;
    const { data: freshBookData, refetch: refetchBookData } = useGetNaray8byId(narrateId);

    const { narrate, content, user, canRead } = chapter ?? {};

    // ✅ FIX #2: Use fresh book data for accurate ratings and view counts
    const {
        coverImage,
        description,
        viewCount,
        ratings,
        createdAt,
        tags,
        title: bookTitle,
        totalChapters: bookTotalChapters
    } = freshBookData ?? narrate ?? {};

    const { userId,  isFollowing: initialIsFollowing } = user ?? {};

    const isAuthor = myId === userId;
    // const authorName = `${firstName || ''} ${lastName || ''}`.trim();

    const [currentFollowStatus, setCurrentFollowStatus] = useState(initialIsFollowing);

    const totalChapters = chapters?.length || bookTotalChapters || 0;

    const [zoomed, setZoomed] = useState(false);
    const [chapterMenuOpen, setChapterMenuOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);

    const [unlockModalOpen, setUnlockModalOpen] = useState(null);
    const [shareModalOpen, setShareModalOpen] = useState(false);

    // Fetch fresh follow status when component mounts
    useEffect(() => {
        const fetchFollowStatus = async () => {
            if (userId && myId && !isAuthor) {
                try {
                    const followStatus = await checkFollowStatus(userId);
                    setCurrentFollowStatus(followStatus);
                } catch (error) {
                    console.error("Error fetching follow status:", error);
                }
            }
        };

        fetchFollowStatus();
    }, [userId, myId, isAuthor]);

    // Update follow status when data refetches
    useEffect(() => {
        setCurrentFollowStatus(initialIsFollowing);
    }, [initialIsFollowing]);

    // ✅ FIX #3: Refresh ALL data when info modal opens
    useEffect(() => {
        if (infoOpen && narrateId) {
            // Refetch chapter data
            refetchChapter();
            // Refetch chapters list
            if (refetchChapters) refetchChapters();
            // ✅ CRITICAL: Refetch fresh book data for accurate ratings and views
            refetchBookData();
            // Invalidate all related queries
            queryClient.invalidateQueries({ queryKey: ["naray8", "chapter", chapterId] });
            queryClient.invalidateQueries({ queryKey: ["naray8", narrateId] });
        }
    }, [infoOpen, narrateId, chapterId, queryClient, refetchChapter, refetchChapters, refetchBookData]);

    const chapterNavInfo = useMemo(() => {

        if (!chapters || chapters?.length === 0) return {};

        const currentIndex = chapters?.findIndex(
            (chptr) => chptr.chapterId === chapterId
        );

        const nextChapterId = chapters[currentIndex + 1]
            ? chapters[currentIndex + 1].chapterId
            : null;

        const prevChapterId =
            currentIndex > 0
                ? chapters[currentIndex - 1].chapterId
                : null;

        return {
            currentIndex,
            nextChapterId,
            prevChapterId
        };

    }, [chapters, chapterId]);

    const scrollRef = useRef(null);

    const handleScroll = () => {

        if (scrollRef.current) {

            localStorage.setItem(
                `naray8:scroll:${chapterId}`,
                scrollRef.current.scrollTop
            );

        }

    };

    useEffect(() => {

        const savedPosition = localStorage.getItem(
            `naray8:scroll:${chapterId}`
        );

        if (scrollRef.current && savedPosition) {
            scrollRef.current.scrollTop = parseInt(savedPosition, 10);
        }

    }, [chapterId]);

    const handlePrevious = () => {

        if (chapterNavInfo.prevChapterId) {

            navigate(
                `/naray8/b/${narrateId}/chapter/${chapterNavInfo.prevChapterId}`,
                { replace: true }
            );

        }

    };

    const handleNext = () => {

        if (chapterNavInfo.nextChapterId) {

            navigate(
                `/naray8/b/${narrateId}/chapter/${chapterNavInfo.nextChapterId}`,
                { replace: true }
            );

        }

    };

    const handleUnlockChapter = () => {
        setUnlockModalOpen({ chapterId: chapterId });
    };

    const handleShare = () => {
        setShareModalOpen(true);
    };

    const handleFollowChange = async (newFollowStatus) => {
        setCurrentFollowStatus(newFollowStatus);
        // Invalidate queries to refresh cached data
        queryClient.invalidateQueries({ queryKey: ["naray8", "chapter", chapterId] });
        queryClient.invalidateQueries({ queryKey: ["naray8", narrateId] });
        queryClient.invalidateQueries({ queryKey: ["naray8-writer"] });
        await refetchChapter();
        await refetchBookData();
    };

    // Create author object with current follow status
    const authorWithFollowStatus = {
        ...user,
        isFollowing: currentFollowStatus
    };

    /* ================= LOCKED ================= */
    if (!canRead && !isAuthor) {

        return (
            <>
                <ChapterPurchaseFlow
                    narray8Id={narrateId}
                    open={unlockModalOpen}
                    setOpen={setUnlockModalOpen}
                    chapters={
                        chapters
                            ?.filter(ch => !ch?.canRead)
                            ?.sort((a, b) => a.chapterNumber - b.chapterNumber) || []
                    }
                />

                <div className="relative min-h-[500px] flex items-center justify-center bg-customGray rounded-[10px] text-white px-4">

                    <Naray8BackButton className="absolute top-4 left-4 z-20" />

                    <div className="text-center max-w-[420px]">

                        <h2 className="text-4xl font-outfit font-semibold mb-3">
                            Chapter Locked 🔒
                        </h2>

                        <p className="text-gray-300 mb-6 font-sora font-semibold">
                            This chapter is only available to premium users.
                        </p>

                        <div className="flex items-center justify-center gap-3 flex-wrap">

                            {chapterNavInfo.prevChapterId && (
                                <button
                                    onClick={handlePrevious}
                                    className="px-5 py-3 border border-[#9B9B9B] rounded-lg bg-white/10 hover:bg-white/20 transition"
                                >
                                    Previous Chapter
                                </button>
                            )}

                            <button
                                onClick={handleUnlockChapter}
                                className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 transition text-black font-semibold rounded-lg"
                            >
                                Unlock Chapter
                            </button>

                        </div>

                    </div>

                </div>
            </>
        );

    }

    return (
        <>
            <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="font-sora bg-customGray rounded-[10px] text-white overflow-hidden"
            >

                {/* ================= BANNER ================= */}
                <section className="relative w-full aspect-[4.7/1] flex justify-center">

                    <Naray8BackButton className={"absolute left-3 top-3 z-30"} />

                    <img
                        src={coverImage}
                        alt="cover"
                        className="absolute inset-0 w-full h-full object-top object-cover"
                    />

                    <div className="absolute inset-0 bg-white/15 z-10" />

                    <img
                        src={coverImage}
                        alt="book"
                        className="min-w-[142px] aspect-[9/16] object-cover z-20"
                    />

                    {/* CHAPTER MENU */}
                    <div className="absolute bottom-3 left-3 z-40">

                        <button
                            onClick={() => setChapterMenuOpen((prev) => !prev)}
                            className={`chapterMenu py-2 text-2xl ${chapterMenuOpen
                                ? "text-customDark hover:text-customDark/40"
                                : "text-white hover:text-white/80"
                                }`}
                        >
                            <GiHamburgerMenu />
                        </button>

                        {chapterMenuOpen && (
                            <ClickOutsideWrapper
                                ignoreSelector={"chapterMenu"}
                                onClose={() => setChapterMenuOpen(false)}
                                className="absolute top-[80%] mt-2 w-[271px] bg-[#1C1B19] shadow-md shadow-black/60"
                            >
                                <div className="max-h-[517px] overflow-y-auto scrollbar-hide">

                                    {chapters?.map((c) => (
                                        <button
                                            key={c.chapterId}
                                            onClick={() => {
                                                navigate(
                                                    `/naray8/b/${narrateId}/chapter/${c.chapterId}`
                                                );

                                                setChapterMenuOpen(false);
                                            }}
                                            className="block w-full px-4 py-2 text-left text-sm hover:bg-[#3A3A36]"
                                        >
                                            {c.title}
                                        </button>
                                    ))}

                                </div>
                            </ClickOutsideWrapper>
                        )}

                    </div>

                    {/* RIGHT ACTIONS */}
                    <div className="absolute bottom-3 right-3 flex gap-3">

                        {/* ZOOM */}
                        <button
                            onClick={() => setZoomed(!zoomed)}
                            className="text-white"
                        >
                            <FaExpand size={24} />
                        </button>

                        {/* INFO */}
                        <div className="relative z-40">

                            <button
                                onClick={() => setInfoOpen(prev => !prev)}
                                className="infoClas bg-customDark p-1 px-8 rounded-[2px] hover:bg-customDark/80 text-white font-outfit font-semibold"
                            >
                                Info
                            </button>

                            {infoOpen && (
                                <ClickOutsideWrapper
                                    ignoreSelector={"infoClas"}
                                    onClose={() => setInfoOpen(false)}
                                    className="fixed inset-0 md:absolute md:top-[80%] md:mt-4 md:right-0 md:inset-auto z-50 flex items-center justify-center md:block bg-black/70 md:bg-transparent"
                                >
                                    {/* Modal Container */}
                                    <div className="bg-customDark rounded-xl md:rounded-none w-[90vw] max-h-[90vh] overflow-y-auto md:w-[632px] md:max-h-none md:overflow-visible p-4 md:p-6 shadow-black/60 shadow-md">

                                        {/* Close Button - Mobile */}
                                        <button
                                            onClick={() => setInfoOpen(false)}
                                            className="float-right mb-2 text-white/70 hover:text-white md:hidden"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>

                                        <div className="clear-both"></div>

                                        {/* DESCRIPTION */}
                                        <section className="text-sm text-white font-sora">
                                            <h1 className="text-[14px] font-semibold">
                                                Description
                                            </h1>
                                            <p className="text-white text-[11px] leading-[14px] font-sora mt-1 break-words">
                                                {description}
                                            </p>
                                        </section>

                                        {/* ✅ FIX #4: STORY INFO with ACCURATE ratings and view counts */}
                                        <section className="mt-4">
                                            <div className="flex flex-wrap gap-2 mt-4 text-sm">
                                                <Badge
                                                    icon={<FaStar size={14} className="text-[#FFBC00]" />}
                                                    text={`${Number(ratings || 0).toFixed(1)} Rating`}
                                                />
                                                <Badge
                                                    icon={null}
                                                    text={`${totalChapters} chapters`}
                                                />
                                                <Badge
                                                    icon={<IoEye size={14} />}
                                                    text={`${formatNumber(viewCount || 0)} Views`}
                                                />
                                                <Badge
                                                    icon={<FaCalendarAlt size={14} />}
                                                    text={moment(createdAt).format("MMMM YYYY")}
                                                />
                                            </div>
                                        </section>

                                        {/* GENRES */}
                                        <section className="mt-4">
                                            <h1 className="text-[12px]">Genres</h1>
                                            <div className="flex flex-wrap gap-2 mt-2 text-[10px]">
                                                {tags?.map((genre, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex items-center text-[9.85px] text-customDark font-sora font-semibold gap-1 bg-[#9B9B9B] px-3 py-0.5 rounded-[4px]"
                                                    >
                                                        {capitalizeFirstLetter(genre)}
                                                    </div>
                                                ))}
                                            </div>
                                        </section>

                                        {/* SOCIAL BUTTONS with correct follow state */}
                                        <section className="mt-4 flex w-full flex-col gap-2">
                                            <div className="flex flex-wrap gap-2">
                                                {!isAuthor && (
                                                    <FollowButton
                                                        author={authorWithFollowStatus}
                                                        onFollowChange={handleFollowChange}
                                                        className="bg-white/15"
                                                    />
                                                )}
                                                <button
                                                    onClick={handleShare}
                                                    className="px-5 py-2 flex items-center justify-center rounded-[5px] bg-white/15 text-white hover:bg-white/25 transition"
                                                    aria-label="Share"
                                                >
                                                    <PiShareFatFill size={18} className="text-[#BE9524]" />
                                                </button>
                                            </div>
                                        </section>

                                    </div>
                                </ClickOutsideWrapper>
                            )}

                        </div>

                    </div>

                </section>

                {/* CONTENT */}
                <div className={`px-4 mt-8 border-b border-white`}>

                    <h1 className="font-sora text-4xl mb-2 text-center">
                        {chapter?.title}
                    </h1>

                    <div
                        className="prose prose-invert max-w-full mt-6"
                        dangerouslySetInnerHTML={{ __html: content }}
                    />

                </div>

                {/* NAVIGATION */}
                <div className="flex px-4 justify-between my-8">

                    <button
                        onClick={handlePrevious}
                        disabled={!chapterNavInfo.prevChapterId}
                        className="px-4 py-2 flex items-center font-sora font-semibold text-[11px] bg-white/[0.09] rounded-[5px] border border-[#9B9B9B] disabled:opacity-50"
                    >
                        <MdKeyboardDoubleArrowLeft
                            size={15}
                            className='text-customGold'
                        />

                        <span className='-mb-0.25'>
                            Previous
                        </span>
                    </button>

                    <button
                        onClick={handleNext}
                        disabled={!chapterNavInfo.nextChapterId}
                        className="px-4 py-2 flex items-center font-sora font-semibold text-[11px] bg-white/[0.09] rounded-[5px] border border-[#9B9B9B] disabled:opacity-50"
                    >
                        <span className='-mb-0.25'>
                            Next
                        </span>

                        <MdKeyboardDoubleArrowRight
                            size={15}
                            className='text-customGold'
                        />
                    </button>

                </div>

                <Narray8Comment chapterId={chapterId} />

            </div>

            {/* SHARE MODAL */}
            {shareModalOpen && (
                <ShareUsersModal
                    open={shareModalOpen}
                    onClose={() => setShareModalOpen(false)}
                    storyTitle={chapter?.title || bookTitle || "Untitled Chapter"}
                    sharedText={(() => {
                        const text = content?.replace(/<[^>]*>/g, '').trim() || "";
                        if (text.length > 0) {
                            return text.substring(0, 500);
                        }
                        return `Check out chapter "${chapter?.title}" from "${bookTitle}" on Narray8!`;
                    })()}
                    contentType="chapter"
                    contentId={chapterId}
                    narrateId={narrateId}
                />
            )}
        </>
    );
}

export default ChapterPage;